(() => {
  const pickerSelector = '[data-product-card-variant-quick-add]';

  const getPicker = (element) => element.closest(pickerSelector);

  const getVariants = (picker) => {
    if (picker.variantQuickAddVariants) return picker.variantQuickAddVariants;

    try {
      picker.variantQuickAddVariants = JSON.parse(
        picker.querySelector('[data-variant-quick-add-variants]').textContent
      );
    } catch (error) {
      picker.variantQuickAddVariants = [];
    }

    return picker.variantQuickAddVariants;
  };

  const getSelections = (picker) => {
    picker.variantQuickAddSelections ||= {};
    return picker.variantQuickAddSelections;
  };

  const getPreviewImage = (picker) => {
    const media = picker.closest('.product-item__media');
    if (!media) return null;

    let preview = media.querySelector('[data-variant-image-preview]');
    if (preview) return preview;

    preview = document.createElement('img');
    preview.className = 'product-card-variant-quick-add__image-preview';
    preview.dataset.variantImagePreview = '';
    preview.alt = '';
    preview.setAttribute('aria-hidden', 'true');
    preview.style.objectFit =
      getComputedStyle(picker).getPropertyValue('--variant-preview-fit').trim() || 'cover';
    media.insertBefore(preview, picker);
    return preview;
  };

  const showVariantImage = (button) => {
    const imageUrl = button.dataset.variantPreviewImage;
    const picker = getPicker(button);
    if (!imageUrl || !picker) return;

    const preview = getPreviewImage(picker);
    if (!preview) return;

    const reveal = () => preview.classList.add('is-visible');
    if (preview.dataset.currentUrl !== imageUrl) {
      preview.classList.remove('is-visible');
      preview.dataset.currentUrl = imageUrl;
      preview.src = imageUrl;
      preview.complete ? reveal() : preview.addEventListener('load', reveal, { once: true });
    } else {
      reveal();
    }
  };

  const hideVariantImage = (button) => {
    const picker = getPicker(button);
    const preview = picker
      ?.closest('.product-item__media')
      ?.querySelector('[data-variant-image-preview]');
    preview?.classList.remove('is-visible');
  };

  const updateOptionAvailability = (picker) => {
    const variants = getVariants(picker);
    const selections = getSelections(picker);

    picker.querySelectorAll('[data-variant-option-value]').forEach((button) => {
      const optionIndex = Number(button.dataset.optionIndex);
      const candidateSelections = { ...selections, [optionIndex]: button.dataset.optionValue };
      const available = variants.some((variant) => {
        if (!variant.available) return false;

        return Object.entries(candidateSelections).every(
          ([index, value]) => variant.options[Number(index)] === value
        );
      });

      button.disabled = !available;
      button.setAttribute(
        'aria-pressed',
        String(selections[optionIndex] === button.dataset.optionValue)
      );
    });
  };

  const applyDefaultColorSelection = (picker) => {
    const selections = getSelections(picker);
    Object.keys(selections).forEach((key) => delete selections[key]);

    const groups = picker.querySelectorAll('[data-variant-option-group]');
    const colorButton = picker.querySelector(
      '.product-card-variant-quick-add__value--swatch:not(:disabled)'
    );
    const sizeButton = picker.querySelector('.product-card-variant-quick-add__value--chip');

    if (groups.length === 2 && colorButton && sizeButton) {
      selections[Number(colorButton.dataset.optionIndex)] = colorButton.dataset.optionValue;
    }

    updateOptionAvailability(picker);
  };

  const initializePicker = (picker) => {
    if (!picker || picker.dataset.variantQuickAddInitialized === 'true') return;

    picker.dataset.variantQuickAddInitialized = 'true';
    applyDefaultColorSelection(picker);
  };

  const resetPicker = (picker) => {
    picker.removeAttribute('aria-busy');
    picker.classList.remove('is-adding');
    picker.querySelector('[data-variant-quick-add-status]').textContent = '';
    applyDefaultColorSelection(picker);
  };

  const addVariant = (picker, variant) => {
    if (picker.classList.contains('is-adding')) return;

    const proxy = picker.querySelector('[data-quick-shop-trigger="quick-add"]');
    const input = picker.querySelector('[data-variant-quick-add-id]');
    const status = picker.querySelector('[data-variant-quick-add-status]');

    input.value = variant.id;
    proxy.dataset.productId = variant.id;
    picker.classList.add('is-adding');
    picker.setAttribute('aria-busy', 'true');
    status.textContent = '...';

    let completed = false;
    const observer = new MutationObserver(() => {
      if (completed || proxy.classList.contains('loading')) return;

      completed = true;
      observer.disconnect();
      status.textContent = 'Added';
      window.setTimeout(() => resetPicker(picker), 900);
    });

    observer.observe(proxy, { attributes: true, attributeFilter: ['class'] });
    proxy.click();

    window.setTimeout(() => {
      if (completed) return;

      completed = true;
      observer.disconnect();
      picker.removeAttribute('aria-busy');
      picker.classList.remove('is-adding');
      status.textContent = window.theme?.strings?.products?.product?.unavailable || 'Unavailable';
    }, 8000);
  };

  const selectOption = (button) => {
    const picker = getPicker(button);
    if (!picker || picker.classList.contains('is-adding') || button.disabled) return;

    initializePicker(picker);

    const selections = getSelections(picker);
    selections[Number(button.dataset.optionIndex)] = button.dataset.optionValue;
    updateOptionAvailability(picker);

    const optionCount = picker.querySelectorAll('[data-variant-option-group]').length;
    if (Object.keys(selections).length !== optionCount) return;

    const variant = getVariants(picker).find(
      (item) =>
        item.available &&
        Object.entries(selections).every(
          ([index, value]) => item.options[Number(index)] === value
        )
    );

    if (variant) addVariant(picker, variant);
  };

  document.addEventListener('click', (event) => {
    const option = event.target.closest('[data-variant-option-value]');
    const picker = getPicker(event.target);

    if (picker) event.stopPropagation();

    if (option) {
      event.preventDefault();
      selectOption(option);
    }
  });

  document.addEventListener('pointerover', (event) => {
    initializePicker(getPicker(event.target));
    const button = event.target.closest('[data-variant-preview-image]');
    if (!button || button.contains(event.relatedTarget)) return;
    showVariantImage(button);
  });

  document.addEventListener('pointerout', (event) => {
    const button = event.target.closest('[data-variant-preview-image]');
    if (!button || button.contains(event.relatedTarget)) return;
    hideVariantImage(button);
  });

  document.addEventListener('focusin', (event) => {
    initializePicker(getPicker(event.target));
    const button = event.target.closest('[data-variant-preview-image]');
    if (button) showVariantImage(button);
  });

  document.addEventListener('focusout', (event) => {
    const button = event.target.closest('[data-variant-preview-image]');
    if (button) hideVariantImage(button);
  });

  const initializeWithin = (root) => {
    if (root.matches?.(pickerSelector)) initializePicker(root);
    root.querySelectorAll?.(pickerSelector).forEach(initializePicker);
  };

  initializeWithin(document);

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) initializeWithin(node);
      });
    });
  }).observe(document.body, { childList: true, subtree: true });
})();
